import { Doc } from '@dadohr/shared/domainTypes/document';
import { MomentTemplateStatus } from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { FS } from '@dadohr/shared/versions';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography
} from '@material-ui/core';
import { Upload } from 'lucide-react';
import ReactHelmet from 'react-helmet';
import { ButtonLoader } from '../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../components/CustomInputLabel';
import { Form } from '../../../../components/Form';
import { FormField } from '../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../components/Form/FormSubmitButton';
import { ImageUploadDialog } from '../../../../components/ImageUpload';
import { LinkExternal } from '../../../../components/LinkExternal';
import { OrgLogo } from '../../../../components/OrgLogo';
import { TimezoneSelector } from '../../../../components/TimezoneSelector';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { updateDocFromPartial } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { guessCurrentTimezone, now } from '../../../../services/time';
import { updateLogo, uploadLogoAndUseIt } from '../../../Org/pages/Branding';

const URLS = {
  TermsOfService: 'https://dadohr.com/legal/terms-and-conditions',
  PrivacyPolicy: 'https://dadohr.com/legal/privacy-policy'
};

type OrganizationValues = {
  name: string;
  timezone: string;
  acceptedToSAndPP: boolean;
};

const OrganizationContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: p.theme.spacing(2),
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  width: 520
}));

const getDomainFromEmail = (s: string) => {
  try {
    return s.substring(s.indexOf('@') + 1);
  } catch {
    return '';
  }
};

export const UpdateOrganizationInfo = () => {
  const { org, email, id: currentUserId } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const { goTo, ROUTES } = useRoutes();
  const initialValues = {
    name: org.name || '',
    timezone: guessCurrentTimezone(),
    acceptedToSAndPP: false
  };
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const { trackEvent } = useProductAnalytics();

  const onSubmit = async ({
    name,
    timezone,
    acceptedToSAndPP
  }: OrganizationValues) => {
    if (!acceptedToSAndPP) {
      return;
    }
    const p: Doc<
      Pick<IOrganization, 'name' | 'tz' | 'termsOfServiceAndPrivacyPolicy'>
    > = {
      id: org.id,
      collection: FS.orgs,
      data: {
        name,
        tz: timezone,
        termsOfServiceAndPrivacyPolicy: {
          accepted: true,
          editedAt: now(),
          editedBy: currentUserId
        }
      }
    };
    await updateDocFromPartial(p).then(() => {
      trackEvent({
        eventType: PAEventType.OTHER,
        name: 'Moments Sign Up Completed',
        Explanation:
          'Admin user completes the sign-up process for moments-only organization',
        'Organization Domain': getDomainFromEmail(email)
      });

      goTo(
        ROUTES.moments.overview.url(MomentTemplateStatus.TEMPLATE, {
          signUpSuccess: true
        })
      );
    });
  };
  return (
    <FlexContainer fullWidth marginTop={4} justifyContent="center">
      <ReactHelmet>
        <title>Dado Moments | Sign up</title>
      </ReactHelmet>
      <OrganizationContainer>
        <Typography component="h3" variant="h3">
          {t('Welcome to Dado!')}
        </Typography>
        <Typography>{t('Add a few more details to get started')}</Typography>
        <Form<OrganizationValues>
          initialValues={initialValues}
          onSubmit={onSubmit}
          className={css({ width: '100%' })}
        >
          {({ submitting, values }) => (
            <FlexContainer
              spacing={2}
              marginTop={2}
              fullWidth
              direction="column"
              alignItems="flex-start"
            >
              <CustomInputLabel fullWidth label={t('Organization Name')}>
                <FormField name="name">
                  {({ input }) => (
                    <TextField
                      {...input}
                      variant="outlined"
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                    />
                  )}
                </FormField>
              </CustomInputLabel>
              <CustomInputLabel fullWidth label={t('Timezone')}>
                <FormField name="timezone">
                  {({ input }) => (
                    <TimezoneSelector
                      fullWidth
                      value={input.value}
                      onChange={input.onChange}
                      disabledClearable
                    />
                  )}
                </FormField>
              </CustomInputLabel>
              <CustomInputLabel
                fullWidth
                label={t('Logo')}
                subLabel={t('Not required but recommended')}
              >
                <FormField name="imageUrl">
                  {({ input }) => (
                    <>
                      {org.logoUrl && (
                        <div>
                          <FlexContainer marginBottom={2}>
                            <OrgLogo d={org} />
                          </FlexContainer>
                          <FlexContainer>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Upload size={20} />}
                              onClick={openDialog}
                            >
                              {t('org.branding.logoReplace')}
                            </Button>
                            <Button
                              size="small"
                              color="secondary"
                              onClick={() => updateLogo(org.id, '')}
                            >
                              {t('org.branding.logoRemove')}
                            </Button>
                          </FlexContainer>
                        </div>
                      )}
                      {!org.logoUrl && (
                        <Button
                          startIcon={<Upload size={18} />}
                          variant="outlined"
                          onClick={openDialog}
                        >
                          {t('org.branding.logoUpload')}
                        </Button>
                      )}
                      <ImageUploadDialog
                        title={t('org.branding.logoUpload')}
                        open={dialogOpen}
                        onCancel={closeDialog}
                        src={org.logoUrl}
                        onUpload={(blob) => uploadLogoAndUseIt(org, blob)}
                      />
                    </>
                  )}
                </FormField>
              </CustomInputLabel>
              <div>
                <Typography component="div" variant="caption">
                  {t('org.branding.logoHint1')}
                </Typography>
                <Typography component="div" variant="caption">
                  {t('Will be shown in emails sent via Moments.')}
                </Typography>
              </div>
              <FormField name="acceptedToSAndPP" type="checkbox">
                {({ input }) => (
                  <FormControlLabel
                    control={<Checkbox color="primary" {...input} />}
                    label={
                      <Typography>
                        {t('I agree to the Dado')}{' '}
                        <LinkExternal
                          color="primary"
                          href={URLS.TermsOfService}
                        >
                          {t('Terms and Conditions')}
                        </LinkExternal>{' '}
                        {t('and')}{' '}
                        <LinkExternal color="primary" href={URLS.PrivacyPolicy}>
                          {t('Privacy Policy')}
                        </LinkExternal>
                      </Typography>
                    }
                  />
                )}
              </FormField>
              <FormSubmitButton
                submitting={submitting}
                submitComponent={
                  <>
                    <ButtonLoader /> {t('Completing...')}
                  </>
                }
                disabled={
                  !values.name || !values.timezone || !values.acceptedToSAndPP
                }
              >
                {t('Complete sign-up')}
              </FormSubmitButton>
            </FlexContainer>
          )}
        </Form>
      </OrganizationContainer>
    </FlexContainer>
  );
};
